import React, {useEffect, useRef, useState} from "react";
import {useSortable} from "@dnd-kit/sortable";
import {CSS} from "@dnd-kit/utilities";
import {getTemplateDefinition} from "../../data/template-catalog";
import {getProductBySlug} from "../../data/products/index";
import Button from "@rescui/button";
import {ExportVisibilityControl} from "../../layout/TemplateControls";
import {knownTemplates} from "../../data/templates";
import {BannerRenderer} from "../BannerRenderer";
import {observeRenderReadiness} from "../render-readiness";
import {type BannerInstance} from "../document";
import {type BannerChange} from "../scope-adapter";
import {PreviewBoundary} from "./PreviewBoundary";
import {useFeature} from "../../hooks/useFeature";

type RendererProps = React.ComponentProps<typeof BannerRenderer>;
type Props = {key?: string; banner: BannerInstance; readOnly?: boolean; selected: boolean; index: number; total: number; onSelect: () => void; onChange: BannerChange;
  onDuplicate: () => void; onRemove: () => void; onMove: (offset: number) => void; onNavigate: (offset: number) => void;
  inspector?: RendererProps["inspector"]; onPendingChange: RendererProps["onPendingChange"]};
export function GridCard(p: Props) {
  const {banner} = p;
  const definition = getTemplateDefinition(banner.templateKey);
  const product = getProductBySlug(banner.product);
  const {attributes, listeners, setNodeRef, setActivatorNodeRef, transform, transition, isDragging} = useSortable({id: banner.instanceId, disabled: p.readOnly});
  const preview = useRef<HTMLDivElement>(null);
  const [resourceError, setResourceError] = useState("");
  // Role feature "banner.export-eye" (data/features.ts). An excluded banner
  // keeps its eye, so it can come back.
  const showEye = useFeature("banner.export-eye") || !banner.exportEnabled;
  useEffect(() => observeRenderReadiness(preview.current, setResourceError), []);
  return <section ref={setNodeRef} style={{transform: CSS.Transform.toString(transform), transition}}
    className={`template-wrap grid-card${!banner.exportEnabled ? " hidden" : ""}${p.selected ? " is-selected" : ""}${isDragging ? " is-dragging" : ""}`}
    data-grid-card={banner.instanceId} data-selected={p.selected} tabIndex={p.selected ? 0 : -1}
    aria-label={`${definition.label}, ${product.label}, banner ${p.index + 1} of ${p.total}`}
    onClick={p.onSelect} onFocusCapture={p.onSelect} onKeyDown={(e) => {
      if (e.target !== e.currentTarget) return;
      if (["ArrowLeft", "ArrowUp", "ArrowRight", "ArrowDown"].includes(e.key)) {e.preventDefault(); p.onNavigate(["ArrowLeft", "ArrowUp"].includes(e.key) ? -1 : 1);}
      if (!p.readOnly && (e.key === "Delete" || e.key === "Backspace")) {e.preventDefault(); p.onRemove();}
    }}>
    <div ref={preview}><PreviewBoundary readOnly={p.readOnly}>
      <BannerRenderer banner={banner} onChange={p.onChange}
        zoom={knownTemplates[definition.channel].find((entry) => entry.key === definition.key).zoom}
        inspector={p.inspector} onPendingChange={p.onPendingChange}
        annotation={<div className="template-annotation">
          <button ref={setActivatorNodeRef} className="label grid-drag-handle" {...attributes} {...listeners}
            aria-label={`Reorder ${definition.label}`} title="Drag to reorder, or press Space and use arrow keys">
            {definition.label} ({definition.width} × {definition.height}) · {product.label}
          </button>
          <div className="template-controls" onClick={(e) => e.stopPropagation()}>
            <Button size="s" mode="clear" aria-label="Move earlier" disabled={p.index === 0} onClick={() => p.onMove(-1)}>←</Button>
            <Button size="s" mode="clear" aria-label="Move later" disabled={p.index === p.total - 1} onClick={() => p.onMove(1)}>→</Button>
            <Button size="s" mode="clear" aria-label="Duplicate banner" onClick={p.onDuplicate}>⧉</Button>
            <Button size="s" mode="clear" aria-label="Remove banner" onClick={p.onRemove}>×</Button>
            {showEye && <ExportVisibilityControl id={banner.instanceId} visible={banner.exportEnabled} onChange={(exportEnabled) => p.onChange(banner.instanceId, {exportEnabled})} />}
          </div>
        </div>} />
    </PreviewBoundary></div>
    {resourceError && <p role="alert">A resource could not be loaded. Check this banner’s images.</p>}
  </section>;
}
